/**
 * Phase 8 — applicant submit auto-transition helper.
 *
 * Locked product decision: the Submit click in the applicant intake
 * sets `status: 'submitted'` AND auto-transitions
 * `caseState: 'submitted' -> 'in_review'`. The `in_review` entry
 * starts the 72-hour SLA countdown shown on the admin case viewer.
 *
 * This helper is intentionally generic over the persistence layer
 * shape so the applicant-web can call it from inside whichever submit
 * handler eventually lands. Splitting it into the firebase-adapter
 * package keeps the admin-web's case-state vocabulary out of the
 * applicant-web's bundle.
 *
 * Source-cited Firebase APIs (verified against
 * `node_modules/@firebase/firestore/dist/index.d.ts`):
 *   - `updateDoc(ref, data)` -> Promise<void>
 *   - `addDoc(col, data)` -> Promise<DocumentReference>
 *   - `collection(firestore, path)` -> CollectionReference
 *   - `doc(firestore, path, id)` -> DocumentReference
 *   - `serverTimestamp()` -> FieldValue
 */
import {
  type Firestore,
  addDoc,
  collection,
  doc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';

const APPLICATIONS = 'applications';

export interface ISubmitApplicationOptions {
  /** UID of the applicant clicking submit. Stamped on the audit row. */
  actorUid: string;
}

/**
 * Marks the application as submitted AND auto-transitions caseState
 * to `in_review`. Writes a `caseStateChanges` audit entry from
 * `submitted -> in_review` so the admin case viewer's audit log
 * shows the transition.
 */
export const submitApplication = async (
  firestore: Firestore,
  applicationId: string,
  options: ISubmitApplicationOptions,
): Promise<void> => {
  const appRef = doc(firestore, APPLICATIONS, applicationId);
  await updateDoc(appRef, {
    status: 'submitted',
    caseState: 'in_review',
    caseStateChangedAt: serverTimestamp(),
    submittedAt: serverTimestamp(),
  });
  const auditCol = collection(
    firestore,
    APPLICATIONS,
    applicationId,
    'caseStateChanges',
  );
  await addDoc(auditCol, {
    type: 'case_state_changed',
    fromState: 'submitted',
    toState: 'in_review',
    byUid: options.actorUid,
    at: serverTimestamp(),
    reason: 'auto_on_submit',
  });
};
